import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { getPage } from "../../lib/api";
import { count, kwh } from "../../lib/format";
import type { Depot } from "../../lib/types";
import { Button } from "../ui/Button";
import { Modal } from "../ui/Modal";
import { Datum, MicroLabel, SectionTitle } from "../ui/Text";

/**
 * One depot at a glance, over the table it was clicked from. Read-only —
 * changing anything goes through Edit, which is a modal of its own.
 *
 * The row is already in hand from the list, so the only request here is the
 * station count: one row of `/charge-points` for this site, read for its total.
 */
export function DepotView({
  depot,
  open,
  canEdit,
  windowLabel,
  onOpenChange,
  onEdit,
}: {
  readonly depot: Depot | undefined;
  readonly open: boolean;
  readonly canEdit: boolean;
  /** The reporting window's label, when one is selected and `usage` is filled. */
  readonly windowLabel: string | undefined;
  readonly onOpenChange: (open: boolean) => void;
  readonly onEdit: (depot: Depot) => void;
}) {
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      eyebrow={depot === undefined ? "Depot" : `Depot · ${depot.timezone}`}
      title={depot?.name ?? "Depot"}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          {depot !== undefined && canEdit ? (
            <Button variant="primary" onClick={() => onEdit(depot)}>
              Edit
            </Button>
          ) : null}
        </>
      }
    >
      {depot === undefined ? (
        <MicroLabel>Loading</MicroLabel>
      ) : (
        <DepotFacts depot={depot} windowLabel={windowLabel} />
      )}
    </Modal>
  );
}

function DepotFacts({
  depot,
  windowLabel,
}: {
  readonly depot: Depot;
  readonly windowLabel: string | undefined;
}) {
  const stations = useQuery({
    queryKey: ["charge-points", { depotId: depot.id, limit: 1 }],
    queryFn: () => getPage("/charge-points", { query: { depotId: depot.id, limit: 1 } }),
  });
  const fleetLink = `/fleet?depotId=${encodeURIComponent(depot.id)}`;

  return (
    <>
      <section className="flex flex-col gap-3">
        <SectionTitle>Site</SectionTitle>
        <dl className="m-0 grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3">
          <Datum label="Name">{depot.name}</Datum>
          <Datum label="Timezone">{depot.timezone}</Datum>
          <Datum label="Stations">
            {stations.isError ? (
              "Could not be counted"
            ) : stations.data === undefined ? (
              "…"
            ) : (
              <Link
                to={fleetLink}
                className="text-signal underline decoration-1 underline-offset-4 hover:no-underline"
              >
                {count(stations.data.meta?.total ?? stations.data.rows.length)}
              </Link>
            )}
          </Datum>
          <div className="col-span-2 sm:col-span-3">
            <Datum label="Id" mono>
              <span className="font-mono">{depot.id}</span>
            </Datum>
          </div>
        </dl>
        <p className="m-0 max-w-prose text-micro tracking-normal normal-case text-ink-faint">
          The timezone is where this site's allowance months and weeks begin. It is fixed once
          stations report against it.
        </p>
      </section>

      <section className="flex flex-col gap-3">
        <SectionTitle>Payments</SectionTitle>
        <dl className="m-0 grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3">
          <Datum label="Provider">
            {depot.paymentProvider === null ? (
              "None — no payments"
            ) : (
              <Link
                to="/payment-providers"
                className="text-signal underline decoration-1 underline-offset-4 hover:no-underline"
              >
                {depot.paymentProvider.label}
              </Link>
            )}
          </Datum>
        </dl>
        {depot.paymentProvider === null ? (
          <p className="m-0 max-w-prose text-micro tracking-normal normal-case text-ink-faint">
            Drivers cannot start a paid charge at this site.
          </p>
        ) : null}
      </section>

      {depot.usage === null || windowLabel === undefined ? null : (
        <section className="flex flex-col gap-3">
          <SectionTitle>Energy · {windowLabel}</SectionTitle>
          <dl className="m-0 grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3">
            <Datum label="Delivered">{kwh(depot.usage.energyWh)} kWh</Datum>
            <Datum label="Closed sessions">{count(depot.usage.closedSessionCount)}</Datum>
          </dl>
        </section>
      )}
    </>
  );
}
